// GridStackExample.tsx
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import StyledText from '../../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../../middleware/ThemeContext';
import { LayoutWidget } from './dndTypes';
import OrganizationsListing from '../OrganizationsListing';
import DraggableIcon from '../../../../assets/icons/DraggableIcon';
import ChevronDownIcon from '../../../../assets/icons/ChevronDownIcon';
import CloseIcon from '../../../../assets/icons/CloseIcon';
import EditIcon from '../../../../assets/icons/EditIcon';
import OrganizationContacts from '../../../organizations/OrganizationOverview/forms/OrganizationContacts';
import { formatDateDefault, formatFieldValue } from '../../../../utils/helper';
import ActivityLog from '../../ActivityLog';
import MediaPreview from '../../../../components/Media/MediaPreview';
import { getSocialIcon } from '../../../../components/SocialIcon';
import OpitonsIcon from '../../../../assets/icons/OptionsIcon';
import DropdownButton from '../../../../components/StyledComponents/StyledDropdownButton';
import RemoveIcon from '../../../../assets/icons/RemoveIcon';
import { useParams, useSearchParams } from 'react-router-dom';
import AddressGrid from '../../../Widget/Addresses';
import WidgetUpdateModal from '../../../../components/Modals/WidgetUpdateModal';
import Badge from '../../../../components/Badge';
import { statusColorMapping } from '../../../../utils/stausMapping';
import { CorporateTheme } from '../../../../utils/themes';
import FileUploadPreview from '../../../../components/Media/FileUploadPreview';


interface GridStackInstance {
    init: (options: any, element: HTMLElement) => any;
}

declare global {
    interface Window {
        GridStack: GridStackInstance & {
            init: (options: any, element: HTMLElement) => any;
        };
    }
}
// Define your API response type
interface Field {
    id: number;
    name: string;
    value: any;
    dataType: string;
    fieldType: string;
    options?: string[];
}

interface WidgetData {
    name: string;
    fields: Field[];
}

interface ApiWidget {
    id: number;
    x: number;
    y: number;
    w: number;
    h: number;
    customTitle: string | null;
    isCollapsed: boolean;
    widgetData: WidgetData;
}

interface contactOrgRoles {
    id: number;
    isPrimary: boolean;
    organization: {
        id: number;
        name: string;
        image: string;
        industry: string | null;
        createdAt: string;
    };
    role: {
        id: number;
        name: string;
    };
}

export type GridStackHandle = {
    onCancel: () => void;

};
// Inject basic styles once

interface GridStackExampleProps {
    isManageLayout?: boolean;
    widgets: LayoutWidget[];
    setWidgets: React.Dispatch<React.SetStateAction<LayoutWidget[]>>;
    data?: any;
    refetch: () => void;
    isLoading?: boolean;
    backupWidget?: LayoutWidget[];
    setIsManagingLayout?: React.Dispatch<React.SetStateAction<boolean>>;
    organization?: any;
    editingPhase?: boolean;
    loading?: boolean;
    widgetRefetch?: () => void;
    scope: string;
    userDataRefetch?: () => void;
    enableWidgetUpdateModal?: boolean;
}

const GridStackExample = forwardRef<GridStackHandle, GridStackExampleProps>(
    (
        { userDataRefetch, widgetRefetch, scope, widgets, setWidgets, backupWidget, setIsManagingLayout, data, refetch, isLoading, isManageLayout, editingPhase = false, loading, organization, enableWidgetUpdateModal = true }: GridStackExampleProps,
        ref
    ) => {
        const { id } = useParams();
        const [searchParams] = useSearchParams();
        const queryId = searchParams.get('id');
        const resolvedRecordId = Number(queryId ?? id);


        const { theme } = useTheme();
        const gridRef = useRef<HTMLDivElement>(null);
        const gridInstanceRef = useRef<any>(null);
        const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
        const [titleValue, setTitleValue] = useState<string>('');
        const [loaded, setLoaded] = useState<boolean>(false);
        const [organizationEdit, setOrganizationEdit] = useState<boolean>(false);
        const [handleSubmit, setHandleSubmit] = useState<boolean>(false);
        const [organizationLoading, setOrganizationLoading] = useState<boolean>(false);
        const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
        const [bulkUpdatePrefillItem, setBulkUpdatePrefillItem] = useState<LayoutWidget | null>(null);
        const onCancel = () => {
            if (!backupWidget || backupWidget.length === 0) {
                setIsManagingLayout?.(false);
                return;
            }

            // 🔥 Pehle React state restore
            setWidgets(backupWidget);

            // 🔥 Phir GridStack restore
            setTimeout(() => {
                const grid = gridInstanceRef.current;
                if (!grid) {
                    setIsManagingLayout?.(false);
                    return;
                }

                try {
                    // Clear existing grid
                    grid.removeAll(false);

                    // DOM update hone do
                    requestAnimationFrame(() => {
                        // Manually add each widget
                        backupWidget.forEach(w => {
                            const element = document.getElementById(`item-${w.widgetId}`);
                            if (element) {
                                grid.makeWidget(element, {
                                    x: w.x,
                                    y: w.y,
                                    w: w.w,
                                    h: w.h,
                                });
                            }
                        });

                        // grid.compact();
                    });
                } catch (error) {
                    console.error('Grid restore error:', error);
                } finally {
                    setIsManagingLayout?.(false);
                }
            }, 0);
        };

        useImperativeHandle(ref, () => ({
            onCancel,
        }));

        useEffect(() => {
            let grid: any = null;

            const loadGridStack = async () => {
                try {
                    // Load GridStack CSS
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/gridstack.js/10.3.1/gridstack.min.css';
                    document.head.appendChild(link);

                    // Load GridStack library
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gridstack.js/10.3.1/gridstack-all.js';
                    script.onload = () => {
                        if (window.GridStack && gridRef.current) {
                            grid = window.GridStack.init({
                                column: 12, // Total 12 columns (3 cards × 4 columns each = 12)
                                cellHeight: 'auto',
                                margin: 12,
                                float: true,
                                animate: true,
                                resizable: {
                                    handles: 'e, se, s, sw, w'
                                },
                                resizeToContent: true,
                            }, gridRef.current);

                            gridInstanceRef.current = grid;
                            setLoaded(true);

                            if (isManageLayout) {
                                grid.enableMove(true);
                                grid.enableResize(true);
                            }
                            else {
                                grid.enableMove(false);
                                grid.enableResize(false);

                            }

                            grid.on('change', (event: Event, changedItems: any[]): void => {
                                if (changedItems && changedItems.length > 0) {
                                    setWidgets(prev => {
                                        const newItems = [...prev];
                                        changedItems.forEach(item => {
                                            // const index = newItems.findIndex(i => i.widgetId === item.id);
                                            const index = newItems.findIndex(i => String(i.widgetId) === String(item.id));

                                            if (index !== -1) {
                                                newItems[index] = {
                                                    ...newItems[index],
                                                    x: item.x,
                                                    y: item.y,
                                                    w: item.w,
                                                    h: item.h
                                                };
                                            }
                                        });
                                        return newItems;
                                    });

                                }
                            });




                        }
                    };


                    document.body.appendChild(script);

                    return () => {
                        if (grid) {
                            grid.destroy(false);
                        }
                    };
                } catch (error) {
                    console.error('Error loading GridStack:', error);
                }
            };

            loadGridStack();
        }, [isManageLayout]);

        const removeWidget = (id: number) => {
            if (gridInstanceRef.current) {
                const element = document.getElementById(`item-${id}`);
                if (element) {
                    gridInstanceRef.current.removeWidget(element, false);
                    // gridInstanceRef.current.compact();
                }
            }

            setWidgets(prev => prev.filter(item => item.widgetId !== id));
        };


        const buildSectionedFields = (fields: any[]) => {
            const result: any[] = [];
            const sectionMap = new Map<string, any>();

            fields?.forEach((item) => {
                if (!item.sectionName) {
                    // normal field → as is
                    result.push({
                        type: 'field',
                        item
                    });
                    return;
                }

                // section field
                if (!sectionMap.has(item.sectionName)) {
                    const sectionBlock = {
                        type: 'section',
                        sectionName: item.sectionName,
                        fields: [item]
                    };

                    sectionMap.set(item.sectionName, sectionBlock);
                    result.push(sectionBlock); // 🔑 correct index pe push
                } else {
                    sectionMap.get(item.sectionName).fields.push(item);
                }
            });

            return result;

        };




        const formatValueByType = (value: any, dataType?: string) => {
            if (!value) return '-';

            switch (dataType) {
                case 'DATE_TIME':
                    return formatDateDefault(value);

                case 'MULTIPLE_CHOICES':
                    return formatFieldValue(value, dataType);

                default:
                    return value;
            }
        };

        const formatCommaSeparatedForWrap = (text: string): React.ReactNode => {
            if (typeof text !== 'string') return text;

            // Split by comma
            const parts = text.split(',');

            return parts.map((part, index) => {
                const trimmed = part.trim();
                if (!trimmed) return null;

                return (
                    <React.Fragment key={index}>
                        {trimmed}
                        {index < parts.length - 1 ? (
                            <>
                                ,<wbr /> {/* Comma + optional line break */}
                            </>
                        ) : null}
                    </React.Fragment>
                );
            });
        };

        const FieldRow = ({ item, primaryEmailType,
            primaryPhoneType,
            primaryAddressType }: {
                item: any, primaryEmailType?: string;
                primaryPhoneType?: string;
                primaryAddressType?: string;
            }) => {
            const { dataType, value, signed_URL } = item?.field || {};
            const isMedia =
                dataType === 'AUDIO_EMBED' || dataType === 'VIDEO_EMBED';
            const iconUrl = getSocialIcon(item?.field?.name); // Get icon URL based on name
            const isFileUpload = dataType === 'FILE_UPLOAD';

            // const isPrimaryEmail =
            //     primaryEmailType &&
            //     item?.field?.name === getPrimaryFieldName(primaryEmailType);

            const getPrimaryFieldName = (type: string): string => {
                switch (type) {
                    case 'Work': return 'Work Email';
                    case 'Personal': return 'Personal Email';
                    case 'Other': return 'Other Email';
                    default: return '';
                }
            };

            const getPrimaryFieldNameForPhone = (type: string): string => {
                switch (type) {
                    case 'Work': return 'Work Phone';
                    case 'Mobile': return 'Mobile Phone';
                    case 'Other': return 'Other Phone';
                    default: return '';
                }
            };

            const getPrimaryFieldNameForAddress = (type: string): string => {
                switch (type) {
                    case 'Work': return 'Work Address';
                    case 'Home': return 'Home Address';
                    case 'Other': return 'Other Address';
                    default: return '';
                }
            };

            // 👇 Helper: Get field type from accessKey or name
            const isEmailField = item.field?.accessKey?.includes('email');
            const isPhoneField = item.field?.accessKey?.includes('phone');
            const isAddressField = item.field?.accessKey?.includes('address');

            // 👇 Determine if this field is primary based on its type
            let isPrimary = false;

            if (isEmailField && primaryEmailType) {
                const fieldName = item.field.name; // e.g. "Other Email"
                const expectedPrimaryName = getPrimaryFieldName(primaryEmailType); // "Other Email" for "Other"
                isPrimary = fieldName === expectedPrimaryName;
            }

            if (isPhoneField && primaryPhoneType) {
                const fieldName = item.field.name; // e.g. "Work Phone"
                const expectedPrimaryName = getPrimaryFieldNameForPhone(primaryPhoneType);
                isPrimary = fieldName === expectedPrimaryName;
            }

            if (isAddressField && primaryAddressType) {
                const fieldName = item.field.name; // e.g. "Home Address"
                const expectedPrimaryName = getPrimaryFieldNameForAddress(primaryAddressType);
                isPrimary = fieldName === expectedPrimaryName;
            }

            // 👇 Helper functions to map type to field name



            return (
                <div className="mb-2">
                    <div className="d-flex gap-1 align-items-center ">

                        {iconUrl && (
                            <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                {iconUrl}
                            </span>
                        )}
                        {(!isFileUpload || item?.field?.value === null) &&
                            <StyledText
                                fontSize="14px"
                                fontWeight={600}
                                textColor={CorporateTheme.colors.textSecondary}
                            >
                                {item?.field?.name}:
                            </StyledText>
                        }

                        {/* {!isMedia && (
                            <StyledText
                                fontSize="14px"
                                fontWeight={400}
                                textColor={theme.CorporateTheme.colors.textSecondary}
                            >
                                {formatValueByType(value, dataType)}
                            </StyledText>
                            
                        )} */}
                        {!isMedia && !isFileUpload && (
                            <div className="d-flex align-items-center gap-2">
                                <StyledText
                                    fontSize="14px"
                                    fontWeight={400}
                                    textColor={CorporateTheme.colors.textSecondary}
                                    style={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                    }}
                                >
                                    {typeof value === 'string' && value.includes(',')
                                        ? formatCommaSeparatedForWrap(value)
                                        : formatValueByType(value, dataType)}
                                </StyledText>

                                {isPrimary ? (
                                    <Badge bgColor={CorporateTheme.colors.green} text="Primary" color={theme.CorporateTheme.colors.white} fontWeight={600} fontSize='12px' borderRadius='38px' />
                                ) : null}
                            </div>
                        )}
                    </div>

                    {isMedia && <MediaPreview url={value} />}
                    {isFileUpload && <FileUploadPreview signedUrl={value} />}
                </div>
            );
        };


        const renderFieldsWithSections = (fields: any[]) => {
            // const primaryEmailTypeField = fields.find(
            //     (f: any) => f.field?.accessKey === 'contact_primary_email_type'
            // );
            // const primaryEmailType = primaryEmailTypeField?.field?.value;
            // const structured = buildSectionedFields(fields);

            // ✅ Email Primary Type
            const primaryEmailTypeField = fields.find(
                f => f.field?.accessKey === 'contact_primary_email_type'
            );
            const primaryEmailType = primaryEmailTypeField?.field?.value;

            // ✅ Phone Primary Type
            const primaryPhoneTypeField = fields.find(
                f => f.field?.accessKey === 'contact_primary_phone_type'
            );
            const primaryPhoneType = primaryPhoneTypeField?.field?.value;

            // ✅ Address Primary Type
            const primaryAddressTypeField = fields.find(
                f => f.field?.accessKey === 'contact_primary_address_type'
            );
            const primaryAddressType = primaryAddressTypeField?.field?.value;

            // Optional: UI se in "Primary Type" fields ko hide karein
            const visibleFields = fields.filter(
                f => ![
                    'contact_primary_email_type',
                    'contact_primary_phone_type',
                    'contact_primary_address_type'
                ].includes(f.field?.accessKey)
            );

            const structured = buildSectionedFields(visibleFields);

            return structured?.map((block, index) => {
                // 👉 Single field (no section)
                if (block?.type === 'field') {
                    return <FieldRow key={block?.item?.id} item={block.item}
                        primaryEmailType={primaryEmailType}
                        primaryPhoneType={primaryPhoneType}
                        primaryAddressType={primaryAddressType}
                    />;
                }

                // 👉 Section block
                return (
                    <div
                        key={`${block.sectionName}-${index}`}
                        style={{
                            border: `1px solid ${theme.CorporateTheme.colors.border}`,
                            borderRadius: 6,
                            marginBottom: 10,
                        }}
                    >
                        {/* Section Header */}
                        <div
                            style={{
                                padding: '12px 16px',
                                borderBottom: `1px solid ${theme.CorporateTheme.colors.border}`,
                                background: theme.CorporateTheme.colors.headerBG,
                                borderRadius: '6px 6px 0 0',
                            }}
                        >
                            <StyledText
                                fontSize="14px"
                                fontWeight={600}
                                textColor={theme.CorporateTheme.colors.primary}
                            >
                                {block.sectionName}
                            </StyledText>
                        </div>

                        {/* Section Fields */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 16px', marginTop: 10 }}>
                            {block?.fields?.map((item: any) => (
                                <FieldRow key={item.id} item={item}

                                    primaryEmailType={primaryEmailType}
                                    primaryPhoneType={primaryPhoneType}
                                    primaryAddressType={primaryAddressType}

                                />
                            ))}
                        </div>
                    </div>
                );
            });
        };


        const renderWidgetBody = (item: LayoutWidget) => {
            const primaryAddressTypeField = item.widget.fields.find(
                f => f.field?.accessKey === 'contact_primary_address_type'
            );
            const primaryAddressType = primaryAddressTypeField?.field?.value;

            // 🔹 Organization Listing widget
            if (item?.widget?.accessKey === 'contact_widget_organizations') {
                return (
                    <OrganizationsListing
                        data={data}
                        refetch={refetch}
                        isLoading={isLoading}
                        editOrganization={organizationEdit}
                        setEditOrganization={setOrganizationEdit}
                        setHandleSubmit={setHandleSubmit}
                        handleSubmit={handleSubmit}
                        setOrganizationLoading={setOrganizationLoading}
                        userDataRefetch={userDataRefetch}
                    />
                );
            }

            if (item?.widget?.accessKey === 'contact_widget_activity_timeline') {
                return (
                    <ActivityLog data={data} />
                );
            }


            if (item?.widget?.accessKey == 'organization_widget_contacts') {
                return (
                    <OrganizationContacts organization={organization} editingPhase={editingPhase} loading={loading} />

                );
            }

            if (item?.widget?.accessKey === 'contact_widget_addresses' || item?.widget?.accessKey === 'organization_widget_addresses') {
                return (
                    <div style={{ height: '100vh' }}>
                        <AddressGrid addresses={item?.widget?.fields} width={item?.w} primaryAddressType={primaryAddressType || ''} />
                    </div>
                );
            }

            // else
            //     return (
            //         <AddressGrid addresses={item?.widget?.fields} width={item?.w} />
            //     );


            // 🔹 Normal widgets (with sections support)
            return renderFieldsWithSections(item?.widget?.fields || []);
        };

        const handleCloseBulkUpdateModal = () => {
            setShowBulkUpdateModal(false);
        };

        const blockedWidgets = [
            'contact_widget_organizations',
            'contact_widget_activity_timeline',
            'organization_widget_contacts',
        ];




        return (
            <div style={{ ...styles.container, padding: isManageLayout ? 10 : 0, background: CorporateTheme.colors.bannerBg }}>
                <div ref={gridRef} className="grid-stack" style={{ visibility: loaded ? 'visible' : 'hidden' }}>
                    {widgets?.map((item: LayoutWidget) => (
                        <div
                            key={item.widgetId}
                            id={`item-${item.widgetId}`}
                            className="grid-stack-item"
                            gs-id={item.widgetId}
                            gs-x={item.x}
                            gs-y={item.y}
                            gs-w={item.w}
                            gs-h={item.h}
                        >

                            <div className="grid-stack-item-content" style={{
                                ...styles.widgetContent, background: CorporateTheme.colors.background, border: `1px solid ${CorporateTheme.colors.border}`
                            }}>
                                <div style={{ ...styles.widgetHeader, position: 'relative', borderBottom: `1px solid ${CorporateTheme.colors.border}` }}>
                                    {/* <StyledText fontSize='16px' fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor}>{item.customTitle ?? item?.widget?.name}</StyledText> */}
                                    {/* {editingTitleId === item.widgetId ? (
                                        <input
                                            value={titleValue}
                                            onChange={(e) => setTitleValue(e.target.value)}
                                            onBlur={() => {
                                                setWidgets(prev =>
                                                    prev.map(w =>
                                                        w.widgetId === item.widgetId ? { ...w, customTitle: titleValue } : w
                                                    )
                                                );
                                                setEditingTitleId(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    setWidgets(prev =>
                                                        prev.map(w =>
                                                            w.widgetId === item.widgetId ? { ...w, customTitle: titleValue } : w
                                                        )
                                                    );
                                                    setEditingTitleId(null);
                                                }
                                            }}
                                            autoFocus
                                            style={{ fontSize: '16px', fontWeight: 700, width: '100%' }}
                                        />
                                    ) : ( */}
                                    <StyledText
                                        fontSize='16px'
                                        fontWeight={700}
                                        textColor={CorporateTheme.colors.headerColor}
                                        style={{ cursor: isManageLayout ? 'default' : 'default' }}
                                    >
                                        {item.customTitle ?? item?.widget?.name}
                                    </StyledText>
                                    {/* )} */}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>


                                        {/* {item?.widget?.accessKey === 'contact_widget_organizations' ? (
                                            organizationEdit ? null : (
                                                <div onClick={() => setOrganizationEdit(true)} className='cursor-pointer'>
                                                    <EditIcon size={18} color={theme.CorporateTheme.colors.heavyMetal} />
                                                </div>
                                            )
                                        ) : (enableWidgetUpdateModal && item?.widget?.accessKey !== 'organization_widget_contacts') ? (
                                            <div
                                                onClick={() => {
                                                    setBulkUpdatePrefillItem(item);
                                                    setShowBulkUpdateModal(true);
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <EditIcon
                                                    size={18}
                                                    color={CorporateTheme.colors.heavyMetal}
                                                />
                                            </div>
                                        ) : null} */}

                                        {['contact_widget_organizations', 'contact_widget_activity_timeline', 'organization_widget_contacts'].includes(item?.widget?.accessKey ?? '') ? null : (
                                            <div
                                                onClick={() => {
                                                    setBulkUpdatePrefillItem(item);
                                                    setShowBulkUpdateModal(true);
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <EditIcon
                                                    size={18}
                                                    color={CorporateTheme.colors.heavyMetal}
                                                />
                                            </div>
                                        )}
                                    </div>


                                </div>
                                <div style={{ ...styles.widgetBody, overflow: 'auto', paddingTop: 20 }}>

                                    {renderWidgetBody(item)}
                                </div>
                            </div>

                        </div>

                    ))}
                </div>

                {enableWidgetUpdateModal && (
                    <WidgetUpdateModal prefillData={bulkUpdatePrefillItem!} show={showBulkUpdateModal} onClose={handleCloseBulkUpdateModal} scope={scope} recordIds={Number.isFinite(resolvedRecordId) ? [resolvedRecordId] : []} refetch={widgetRefetch} ContactDetailsRefetch={refetch} userDetailsRefetch={userDataRefetch} />
                )}
            </div>

        )
    });


const styles = {

    container: {

        minHeight: '100vh',
        height: 'auto',
    },
    header: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#333',
        margin: 0
    },
    subtitle: {
        color: '#666',
        marginTop: '0.5rem',
        marginBottom: 0
    },
    button: {
        padding: '0.75rem 1.5rem',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    buttonDisabled: {
        backgroundColor: '#6c757d',
        cursor: 'not-allowed'
    },
    widgetContent: {
        borderRadius: '8px',
        // padding: '1.25rem',
        Padding: '10px',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        boxShadow: '0px 2px 10px 0px #0000000D',


    },
    widgetHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '18px 20px'
    },
    widgetBody: {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        gap: '10px',
        padding: '10px 20px'
    },
    widgetTitle: {
        color: '#fff',
        fontSize: '1.25rem',
        fontWeight: 'bold',
        margin: 0
    },
    closeButton: {
        border: 'none',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        fontSize: '1.25rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        position: 'absolute' as const,
        top: '-9px',
        right: '-4px',
        zIndex: '99999999999999999'
    },
    widgetInfo: {
        backgroundColor: 'rgba(0,0,0,0.2)',
        color: '#fff',
        fontSize: '0.875rem',
        padding: '0.75rem',
        borderRadius: '6px',
        marginTop: 'auto'
    },
    loading: {
        textAlign: 'center' as const,
        padding: '3rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    instructions: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '1.5rem',
        marginTop: '1.5rem'
    },
    instructionTitle: {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '1rem'
    },
    instructionList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    instructionItem: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '0.75rem',
        color: '#555'
    },
    icon: {
        marginRight: '0.75rem',
        fontSize: '1.25rem'
    }
};

export default GridStackExample;